import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal, Button, Icon, Segment, Input, Header, Message, Dimmer, Loader } from 'semantic-ui-react';
import PaymentSourceDisplay from '../../../external-components/payments/steps/PaymentSourceDisplay';

const EditWalletItemModal = ({ walletItem, open, payment, actions, onOpen, onClose, onSave }) => {
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(false);

	const accountTypeOptions = [
		{ text: 'Checking', value: 1 },
		{ text: 'Savings', value: 2 }
	];

	const defaultData = useMemo(() => {
		return {
			nickname: walletItem.nickname,
			cardExpireMonth: '',
			cardExpireYear: '',
			securityCode: '',
			zipCode: '',
			firstName: walletItem.firstName || '',
			lastName: walletItem.lastName || '',
			accountType: walletItem.bankAccountType === "Checking" ? 1 : 2,
			fundingType: walletItem.fundingAccountType === "Business" ? 2 : 1
		};
	}, [walletItem]);

	const [data, setData] = useState(defaultData);

	const customSettings = useMemo(() => {
		return { ...payment.settings, allowUseOfWalletItems: false };
	}, [payment.settings]);

//	const [isValid, setIsValid] = useState(false);

	const _onSave = useCallback(async () => {
        // set default nickname
		if (!data.nickname) {
            data.nickname = walletItem.fundingAccountLastFourDigits;
        }

		setLoading(true);
		try {
			setError(false);
			await actions.updatePaymentWalletItem(walletItem.walletId, walletItem.walletItemId, { ...walletItem, ...data })
		} catch {
			setError(true);
		}
		setLoading(false);
		if (onSave) onSave();
	}, [actions, data, onSave, walletItem]);

	const _onOpen = useCallback(() => {
		setData(defaultData);
		if (onOpen) onOpen();
	}, [defaultData, onOpen]);

	const isCredit = useMemo(() => walletItem.fundingCategory !== 'Check', [walletItem.fundingCategory]);

	const onChange = useCallback(
		(name, value) => {
			setData({ ...data, [name]: value });
		},
		[data]
	);

	const isValid = useMemo(() => {
		if(isCredit) {
            return data.cardExpireMonth !== '' && data.cardExpireYear !== '' && (!customSettings.collectSecurityCodeForRecurringCreditCardPayments || data.securityCode !== '') && (!customSettings.collectZipCodeForRecurringCreditCardPayments || data.zipCode !== '');
		} else {
			if (data.fundingType === 2) {
				return data.lastName !== '' && data.accountType !== '';
			}
			else {
				return data.firstName !== '' && data.lastName !== '' && data.accountType !== '';
			}
		}
	}, [customSettings, data, isCredit])

	const months = useMemo(
		() =>
			Array.from(Array(13).keys())
				.slice(1)
				.map(o => {
					return { text: o, value: o };
				}),
		[]
	);

	const years = useMemo(
		() =>
			Array.from(Array(10).keys())
				.map(o => o + new Date().getFullYear())
				.map(o => {
					return { text: o, value: o };
				}),
		[]
	);

	return (
		<Modal
			size='tiny'
			onOpen={_onOpen}
			onClose={onClose}
			open={open}
			trigger={
				<span style={{ cursor: 'pointer' }}>
					<Icon link name='edit' />
					Edit
				</span>
			}>
			<Modal.Header>Edit Wallet Item</Modal.Header>
			<Modal.Content>
				{loading && (
					<Dimmer active inverted>
						<Loader active size='massive'>
							Updating Wallet Item...
						</Loader>
					</Dimmer>
				)}
				{error && <Message error>Failed to update wallet item</Message>}
				<Segment>
					<Header>Existing Wallet Item</Header>
					<PaymentSourceDisplay source={{ fiserv: { ...walletItem } }} />
				</Segment>
				<Form>
					<Form.Input label='Nickname' value={data.nickname || ''} onChange={(_, { value }) => onChange('nickname', value)} />
					{isCredit && (
						<Form.Group>
							<Form.Dropdown required name='cardExpireMonth' compact selection options={months} label='Expiration Month' defaultValue={data.cardExpireMonth} onChange={(_, { value }) => onChange('cardExpireMonth', value)} />
							<Form.Dropdown required name='cardExpireYear' compact selection options={years} label='Expiration Year' defaultValue={data.cardExpireYear} onChange={(_, { value }) => onChange('cardExpireYear', value)} />
						</Form.Group>
					)}
					{isCredit && customSettings.collectSecurityCodeForRecurringCreditCardPayments && (
						<Form.Field required>
							<label>Security Code</label>
							<Input value={data.securityCode || ''} onChange={(_, { value }) => onChange('securityCode', value)} type='password' />
						</Form.Field>
					)}
					{isCredit && customSettings.collectZipCodeForRecurringCreditCardPayments && (
						<Form.Field required>
							<label>Zip Code</label>
							<Input value={data.zipCode || ''} onChange={(_, { value }) => onChange('zipCode', value)} type="number" />
						</Form.Field>
					)}

					{!isCredit && 
						<React.Fragment>
							<Form.Dropdown name='accountType' selection label='Account Type' placeholder='Account Type' options={accountTypeOptions} defaultValue={data.accountType} onChange={(_, { value }) => onChange('accountType', value)} />
							{data.fundingType !== 2 && (
								<Form.Input name='firstName' label='First Name' defaultValue={data.firstName} onChange={(_, { value }) => onChange('firstName', value)} />
							)}
							<Form.Input required name='lastName' label={data.fundingType === 2 ? 'Business Name' : 'Last Name'} defaultValue={data.lastName} onChange={(_, { value }) => onChange('lastName', value)} />
						</React.Fragment>
					}
					
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose}>Cancel</Button>
				<Button primary onClick={_onSave} disabled={!isValid}>
					Save
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

EditWalletItemModal.propTypes = {
	open: PropTypes.bool.isRequired,
	payment: PropTypes.object.isRequired
};

export default EditWalletItemModal;
